import { useState, useRef } from "react";
import { Star, Upload, X, Loader2 } from "lucide-react";
import { useFetchReviewsQuery, useCreateReviewMutation } from "../../../stores/api/reviews.api";
import { ToastMessage } from "../../../Components/Utils";
import Img from "./Img";

const StarRating = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="focus:outline-none"
        aria-label={`Rate ${n}`}
      >
        <Star
          size={24}
          className={n <= value ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
        />
      </button>
    ))}
  </div>
);

const ReviewCard = ({ review }) => (
  <div className="border border-gray-100 rounded-lg p-5 space-y-3">
    <div className="flex items-center justify-between">
      <span className="font-semibold text-sm text-gray-800">{review.customer_name}</span>
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            size={13}
            className={n <= review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}
          />
        ))}
      </div>
    </div>
    <p className="text-sm text-gray-600 leading-relaxed">{review.description}</p>
    {review.reply_message && (
      <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 space-y-1">
        <p className="text-xs font-semibold text-gray-500">Reply from Ukasha Enterprise</p>
        <p className="text-sm text-gray-600 leading-relaxed">{review.reply_message}</p>
      </div>
    )}
    {review.images?.length > 0 && (
      <div className="flex gap-2 flex-wrap">
        {review.images.map((src, i) => (
          <div key={i} className="w-20 h-20 rounded overflow-hidden border border-gray-100">
            <Img src={src} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
    )}
    <p className="text-xs text-gray-400">{new Date(review.createdAt).toLocaleDateString()}</p>
  </div>
);

const ReviewSection = ({ productId }) => {
  const { data: reviews = [], isLoading: reviewsLoading } = useFetchReviewsQuery(productId, { skip: !productId });
  const [createReview, { isLoading: submitting }] = useCreateReviewMutation();

  const [rating, setRating] = useState(0);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileRef = useRef(null);

  const onFiles = (e) => {
    const files = Array.from(e.target.files ?? []);
    const combined = [...images, ...files].slice(0, 6);
    setImages(combined);
    const urls = combined.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    e.target.value = "";
  };

  const removeImg = (i) => {
    setImages((prev) => prev.filter((_, idx) => idx !== i));
    setPreviews((prev) => prev.filter((_, idx) => idx !== i));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!rating) return ToastMessage({ type: "error", title: "Rating required", message: "Please select a star rating." });

    const fd = new FormData();
    fd.append("product_id", productId);
    fd.append("customer_name", name);
    fd.append("description", description);
    fd.append("rating", rating);
    images.forEach((f) => fd.append("images", f));

    try {
      await createReview(fd).unwrap();
      ToastMessage({ type: "success", title: "Thank you!", message: "Review submitted and pending approval." });
      setRating(0); setName(""); setDescription(""); setImages([]); setPreviews([]);
    } catch (err) {
      ToastMessage({ type: "error", title: "Error", message: err?.data?.message ?? "Something went wrong." });
    }
  };

  return (
    <div className="space-y-10">
      {/* Reviews list */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-900">
          Customer Reviews {reviews.length > 0 && <span className="text-gray-400 font-normal">({reviews.length})</span>}
        </h3>
        {reviewsLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Loader2 size={14} className="animate-spin" /> Loading reviews…
          </div>
        ) : reviews.length === 0 ? (
          <p className="text-sm text-gray-400">No reviews yet. Be the first!</p>
        ) : (
          <div className="space-y-4">
            {reviews.map((r) => <ReviewCard key={r.id} review={r} />)}
          </div>
        )}
      </div>

      {/* Submit form */}
      <div className="border-t border-gray-100 pt-8">
        <h3 className="text-sm font-semibold text-gray-900 mb-5">Write a Review</h3>
        <form onSubmit={onSubmit} className="space-y-5">
          {/* Rating */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700">Rating *</label>
            <StarRating value={rating} onChange={setRating} />
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700">Your Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="John Doe"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-gray-400 dark:bg-slate-800 dark:border-slate-600 dark:text-gray-100"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700">Review *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={4}
              placeholder="Share your experience with this product…"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 resize-none focus:outline-none focus:border-gray-400 dark:bg-slate-800 dark:border-slate-600 dark:text-gray-100"
            />
          </div>

          {/* Images */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-700">Photos (optional, max 6)</label>
            <div className="flex flex-wrap gap-3">
              {previews.map((src, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 group">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImg(i)}
                    className="absolute inset-0 bg-black/40 hidden group-hover:flex items-center justify-center"
                  >
                    <X size={16} className="text-white" />
                  </button>
                </div>
              ))}
              {previews.length < 6 && (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-gray-400 transition-colors"
                >
                  <Upload size={16} />
                  <span className="text-[10px]">Add photo</span>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-gray-700 disabled:opacity-60 transition-colors"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? "Submitting…" : "Submit Review"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReviewSection;
